import { Account } from './schema/resources/account';
import { Activity } from './schema/resources/activities';
import { Approval } from './schema/resources/approval';
import { Asset } from './schema/resources/asset';
import { ChangeRequest } from './schema/resources/change-request';
import { Comment } from './schema/resources/comment';
import { ContentTreeNode } from './schema/resources/content-tree-node';
import { Contract } from './schema/resources/contract';
import { ContractKey } from './schema/resources/contract-key';
import { ContractKeyGroup } from './schema/resources/contract-key-group';
import { ContractValue } from './schema/resources/contract-value';
import { ContractType } from './schema/resources/contractType.ts';
import { Download } from './schema/resources/download';
import { ExportBatch } from './schema/resources/export-batch';
import { ExportTarget } from './schema/resources/export-target';
import { ExportTargetDefault } from './schema/resources/export-target-default';
import { ExportTargetType } from './schema/resources/export-target-type';
import { Favorite } from './schema/resources/favorite';
import { FeatureFlag } from './schema/resources/featureFlag';
import { Upload } from './schema/resources/file-upload';
import { Upload as Upload2 } from './schema/resources/file-upload2';
import { Folder } from './schema/resources/folder';
import { Generation } from './schema/resources/generation';
import { GenerativeModal } from './schema/resources/generative-model';
import { Invitation } from './schema/resources/invitation';
import { Library } from './schema/resources/library';
import { MetadataCategory } from './schema/resources/metadata-category';
import { MetadataField } from './schema/resources/metadata-field';
import { MetadataFieldType } from './schema/resources/metadata-field-type';
import { MetadataValue } from './schema/resources/metadata-value';
import { Oauth2 } from './schema/resources/oauth2';
import { Oauth2Provider } from './schema/resources/oauth2-provider';
import { Privilege } from './schema/resources/privilege';
import { Project } from './schema/resources/project';
import { ProjectMember } from './schema/resources/project-member';
import { ProjectTeamMembership } from './schema/resources/project-team-membership';
import { ProjectTemplate } from './schema/resources/project-template';
import { Properties } from './schema/resources/properties';
import { Recipient } from './schema/resources/recipient';
import { RenderBatch } from './schema/resources/render-batch';
import { RenderPreset } from './schema/resources/render-preset';
import { Role } from './schema/resources/role';
import { SearchResult } from './schema/resources/search-result';
import { SearchResult2 } from './schema/resources/search-result-2';
import { Share } from './schema/resources/share';
import { Snapshot } from './schema/resources/snapshot';
import { Tag } from './schema/resources/tag';
import { TagCategory } from './schema/resources/tag-category';
import { Team } from './schema/resources/team';
import { Thumbnail } from './schema/resources/thumbnail';
import { TransferOwnership } from './schema/resources/transfer-ownership';
import { Url } from './schema/resources/url';
import { User } from './schema/resources/user';
import { Validation } from './schema/resources/validation';
import { Validator } from './schema/resources/validator';
import { Workspace } from './schema/resources/workspace';
import { WorkspacePreferences } from './schema/resources/workspace-preference';
import { Invoice } from './schema/resources/invoice';

export interface ResourceTypeMap {
  account: Account;
  asset: Asset;
  'change-request': ChangeRequest;
  'content-tree-node': ContentTreeNode;
  contract: Contract;
  'contract-type': ContractType;
  'contract-key-group': ContractKeyGroup;
  'contract-value': ContractValue;
  'contract-key': ContractKey;
  'file-upload': Upload | Upload2;
  folder: Folder;
  invitation: Invitation;
  library: Library;
  'metadata-category': MetadataCategory;
  'metadata-value': MetadataValue;
  'metadata-field': MetadataField;
  'metadata-field-type': MetadataFieldType;
  project: Project;
  'project-member': ProjectMember;
  'project-template': ProjectTemplate;
  'project-team-membership': ProjectTeamMembership;
  properties: Properties;
  recipient: Recipient;
  'review-link': Reviewlink;
  role: Role;
  'search-result': SearchResult;
  share: Share;
  user: User;
  workspace: Workspace;
  'workspace-preference': WorkspacePreferences;
  activity: Activity;
  'feature-flag': FeatureFlag;
  test: Test;
  svg: SVGImage;
  download: Download;
  'render-preset': RenderPreset;
  'render-batch': RenderBatch;
  privilege: Privilege;
  tag: Tag;
  'tag-category': TagCategory;
  'export-target-type': ExportTargetType;
  'export-target': ExportTarget<any>;
  'export-target-default': ExportTargetDefault<any>;
  'export-batch': ExportBatch<any>;
  oauth2: Oauth2;
  'oauth2-provider': Oauth2Provider;
  team: Team;
  validator: Validator;
  validation: Validation;
  comment: Comment;
  approval: Approval;
  favorite: Favorite;
  url: Url;
  snapshot: Snapshot;
  'search-result-2': SearchResult2;
  'transfer-ownership': TransferOwnership;
  thumbnail: Thumbnail;
  // generative ai
  'generative-model': GenerativeModal;
  generation: Generation;
  invoice: Invoice;
}

export type ResourceType = keyof ResourceTypeMap;

export type Related<T extends ResourceType> = ResourceIdentifier<T> & {
  data: ResourceIdentifier<T>;
};

export interface ResourceBase {
  id?: string;
  created_at?: string;
  deleted_at?: string;
  updated_at?: string;
  type?: ResourceType;
  trashed_at?: string;
  relationships?: { [key: string]: Related<any> | Related<any>[] };
}

export interface ResourceIdentifier<T extends ResourceType = ResourceType> {
  id: string;
  type: T;
}
export interface RelationshipIdentifier<
  T extends ResourceType = ResourceType
> extends ResourceIdentifier<T> {
  relationship: string;
}

export interface PublicResource extends ResourceBase {
  display_image?: string;
  display_name: string;
}
export enum VerifyType {
  'never' = 'never',
  'everyone' = 'everyone',
  'recipients_only' = 'recipients_only'
}

export type ShareIntentType = 'review' | 'review-legacy' | 'share' | 'request';
/**  base type for share, reviewlink and filerequest  */
export interface ShareBase extends ResourceBase {
  creator: string;
  // required in file-request
  display_name?: string;
  display_image?: string;
  // custom message
  message_enabled: boolean;
  message?: string;
  // url token to access by
  token?: string;
  // recipients
  email_addresses?: string[];
  users: { email_address: string; profile_picture?: string }[];
  // verify user based on active session
  verify_user: boolean;
  verify: VerifyType;
  allow_request_access: boolean;
  // disable link after date
  expires: boolean;
  expires_at?: string;

  intent: ShareIntentType;

  // review link: current recipient
  actor_email?: string;

  relationships: {
    node: Related<'content-tree-node'>;
    workspace: Related<'workspace'>;
    'render-presets': Related<'render-preset'>;
  };
}
export interface FileRequest extends ShareBase {}
export interface Reviewlink extends ShareBase {
  display_image?: string;
}

export interface SVGImage extends ResourceBase {
  id: string;
  origin_type: string;
  origin: string;
  svg: string;
}

// Content
export interface ContentTree extends ResourceBase {
  root_folder: {
    uuid: string;
  };
  tree_for: 'Library' | 'Project';
}

export const ALL_FEATURES = [
  '/WORKSPACE/GENERATIVE',
  '/WORKSPACE/PROJECTS',
  '/WORKSPACE/LIBRARY',
  '/WORKSPACE/METADATA',
  '/WORKSPACE/COMPOSE',
  '/WORKSPACE/CONTRACTS',
  '/WORKSPACE/USER_MANAGEMENT',
  '/WORKSPACE/TEAMS',
  '/WORKSPACE/FAVORITES',
  '/WORKSPACE/MEILISEARCH',
  '/WORKSPACE/PROJECTS/ACTIVITIES',
  '/WORKSPACE/ACTIVITIES',
  '/WORKSPACE/SHARING/SHARE_LINKS',
  '/WORKSPACE/SHARING/REVIEW_LINKS',
  '/WORKSPACE/SHARING/REVIEWS',
  '/WORKSPACE/SHARING/WATERMARK',
  '/WORKSPACE/SHARING/FILE_REQUESTS',
  '/WORKSPACE/SHARING/MAGIC_LINK',
  '/WORKSPACE/EXPORTS/BROADCAST_LEGACY',
  '/WORKSPACE/EXPORTS/YOUTUBE',
  '/WORKSPACE/EXPORTS/META',
  '/WORKSPACE/EXPORTS/CM360',
  '/WORKSPACE/EXPORTS/GOOGLEADS',
  '/WORKSPACE/EXPORTS/CONTENTFUL',
  '/WORKSPACE/EXPORTS/BROADCAST',
  '/WORKSPACE/EXPORTS/RADIO',
  '/WORKSPACE/EXPORTS/TIKTOK',
  '/WORKSPACE/EXPORTS/DEBUG',
  '/WORKSPACE/IMPORTS/DROPBOX',
  '/WORKSPACE/IMPORTS/FRAMEIO',
  '/WORKSPACE/TAGS',
  '/WORKSPACE/TAGS/MODIFY',
  '/WORKSPACE/ADMINCENTER',
  '/WORKSPACE/PROJECTS/NAVIGATION/LIST',
  '/WORKSPACE/PROJECTS/NAVIGATION/METADATA',
  '/WORKSPACE/PROJECTS/NAVIGATION/ACTIVE',
  '/WORKSPACE/PROJECTS/NAME_STRING_TEMPLATE',
  '/WORKSPACE/NAVIGATION/QUICK_NAV',
  '/WORKSPACE/SUPPORT/PHONE',
  '/WORKSPACE/COMPOSE/NEXT',
  '/WORKSPACE/LABELING',
  '/WORKSPACE/FORMS'
] as const;

export type Feature = (typeof ALL_FEATURES)[number];

interface KeyValue {
  key: string;
  value: string;
}

export interface Property extends ResourceBase {
  source: string;
  fields: KeyValue[];
  display_name: string;
}

export type Test = ResourceBase;

export * from './types';
